/*
 * File: transformDriveHistoryItems.ts
 * Author: Austin Robinson
 * Date Created: 12/07/2025
 */
import dayjs from 'dayjs';
import type { SerializedEditorState } from 'lexical';

import type { StudentPeriodEnrollment } from 'types/students';

/**
 * Shape of a transformed drive history item used by the UI.
 */
export interface DriveHistoryItemData {
  /** The period enrollment ID */
  id: string;

  /** The drive label (usually the period number) */
  driveLabel: string;

  /** Formatted date string, e.g. "Feb 7, 2025" */
  date: string;

  /** Formatted start time string, e.g. "3:30 PM" */
  startTime: string;

  /** Formatted end time string, e.g. "4:30 PM" */
  endTime: string;

  /** Array of instructor names */
  instructors: string[];

  /** Attendance status */
  attendance: StudentPeriodEnrollment['attendance'];

  /** Whether the period was canceled */
  isCanceled: boolean;

  /** Rich text notes (Lexical) */
  notes: SerializedEditorState | null;
}

/**
 * Transforms raw period enrollment data into formatted drive history items.
 * Extracts all date/time formatting and mapping logic out of UI components.
 *
 * @param periodEnrollments - List of raw StudentPeriodEnrollment objects.
 * @returns Array of formatted drive history items ready for rendering.
 */
export function transformDriveHistoryItems(periodEnrollments: StudentPeriodEnrollment[]): DriveHistoryItemData[] {
  return periodEnrollments.map((periodEnrollment) => {
    const start = dayjs(periodEnrollment.course_period?.start);
    const end = dayjs(periodEnrollment.course_period?.end);

    const instructors =
      periodEnrollment.course_period?.instructors.map(
        (instructor) => instructor.formal_name ?? `${instructor.first_name} ${instructor.last_name}`,
      ) ?? [];

    return {
      id: periodEnrollment.course_period_id,
      driveLabel: periodEnrollment.period_number ?? '',

      // Date in readable form
      date: start.format('MMM D, YYYY'),

      // Handle time formatting internally
      startTime: start.format('h:mm A'),
      endTime: end.isValid() ? end.format('h:mm A') : '',

      instructors,
      attendance: periodEnrollment.attendance,
      isCanceled: !!periodEnrollment.canceled_at,
      notes: periodEnrollment.notes,
    };
  });
}
